import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatListModal from './ChatListModal';
import ChatSidebar from './ChatSidebar';
import ChatWindow from './ChatWindow';
import EmptyChat from './EmptyChat';
import { useSocket } from '../../context/SocketContext';
import useGetChatList from '../../hooks/chats/useGetChatList';

const Chats = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [needsAttentionRefreshTrigger, setNeedsAttentionRefreshTrigger] = useState(0);
  const [chatListRefreshTrigger, setChatListRefreshTrigger] = useState(0); 
  
  // Get location and navigate for URL parameters
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get socket context
  const { isConnected, emit } = useSocket();
  
  // We need the chat list to find a chat by ID
  const { chatList, loading } = useGetChatList();

  // Check for openChat parameter on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openChatId = searchParams.get('openChat');
    
    if (openChatId && !selectedChat) {
      // Convert to number 
      const chatIdNum = Number(openChatId);
      
      // If chatList is loaded, find and select the chat
      if (chatList && chatList.length > 0) {
        const chatToOpen = chatList.find(chat => chat.chat_id === chatIdNum);
        if (chatToOpen) {
          // console.log(`Opening chat with ID ${chatIdNum} from URL parameter`);
          setSelectedChat(chatToOpen);
          
          // Remove the parameter from URL without reloading
          navigate('/chats', { replace: true });
        } else {
          // console.log(`Chat with ID ${chatIdNum} not found`);
        }
      }
    }
  }, [chatList, location.search, navigate, selectedChat]);

  // Only refresh needs attention list when needed
  const refreshNeedsAttention = () => {
    // console.log("Refreshing needs attention list");
    setNeedsAttentionRefreshTrigger(prev => prev + 1);
  };

  // New function to refresh chat list
  const refreshChatList = () => {
    // console.log("Explicitly refreshing chat list");
    setChatListRefreshTrigger(prev => prev + 1);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    setMessage(''); // Clear message input when changing chats
  };

  const handleNewContactSelect = (contact) => {
    // Create a new chat object with the selected contact
    const newChat = {
      // This is a new chat, so we don't have a chat_id yet
      // It will be assigned by the server after join-room
      chat_id: null,
      // The other member is the selected contact
      other_member: {
        first_name: contact.first_name,
        last_name: contact.last_name,
        image_path: contact.image_path,
      },
      // We need the other_member_id for socket communication
      other_member_id: contact.company_employee_id,
      // Initialize with an empty chat object
      chat: {
        last_message_at: new Date().toISOString(),
        last_chat_message: null
      }
    };
    
    // Set as the selected chat
    setSelectedChat(newChat);
    
    // Close the modal
    handleCloseModal();
    
    // Clear any existing message
    setMessage('');
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  // Function to close the chat - now also emits leave-room
  const closeChat = () => {
    // If we have a selected chat with chat_id, emit leave-room before clearing
    if (selectedChat && selectedChat.chat_id && isConnected) {
      // console.log(`Explicitly leaving chat room with ID: ${selectedChat.chat_id}`);
      emit("leave-room", { chat_id: selectedChat.chat_id });
      // No need to track here since the component will unmount and clean up
    }
    setSelectedChat(null);
  };

  return (
    <div className="flex h-[calc(100vh-100px)] border border-gray-200 rounded overflow-hidden">
      <ChatSidebar 
        selectedChat={selectedChat}
        handleOpenModal={handleOpenModal}
        handleChatSelect={handleChatSelect}
        needsAttentionRefreshTrigger={needsAttentionRefreshTrigger}
        chatListRefreshTrigger={chatListRefreshTrigger}
        refreshNeedsAttention={refreshNeedsAttention}
      />

      {/* Main Chat Area */}
      {selectedChat ? (
        <ChatWindow 
          selectedChat={selectedChat}
          message={message}
          setMessage={setMessage}
          handleMessageChange={handleMessageChange}
          closeChat={closeChat}
          refreshNeedsAttention={refreshNeedsAttention}
          refreshChatList={refreshChatList}
        />
      ) : (
        <EmptyChat />
      )}

      {/* Chat List Modal */}
      <ChatListModal 
        open={openModal} 
        onClose={handleCloseModal} 
        onSelectContact={handleNewContactSelect} 
      />
    </div>
  );
};

export default Chats;